/**
 * Alpine component around the customer's signature ending. signaturePad keeps the
 * drawing; this keeps the code field and carries the signature across the page load
 * between Email me a code and Sign proposal, since nothing is stored on the server
 * until the customer signs. Losing it costs a redraw, nothing more.
 */
export const CODE_LENGTH = 6;

export function cleanCode(value) {
  return String(value).replace(/\D/g, "").slice(0, CODE_LENGTH);
}

export function codeBoxes(code) {
  return Array.from({ length: CODE_LENGTH }, (_, index) => code[index] ?? "");
}

export function readSaved(storage, key) {
  try {
    return JSON.parse(storage.getItem(key)) ?? null;
  } catch {
    return null;
  }
}

export function writeSaved(storage, key, saved) {
  try {
    storage.setItem(key, JSON.stringify(saved));
  } catch {
    // Private mode, or a drawing over the quota: the customer signs again instead.
  }
}

export default function registerProposalSignature(Alpine) {
  Alpine.data("proposalSignature", ({ storageKey, restore, focusOnLoad }) => ({
    code: "",
    ready: false,
    _onVisible: null,

    init() {
      this.ready = true;
      if (!focusOnLoad) return;

      this.focusCode();
      this._onVisible = () => {
        if (document.visibilityState === "visible") this.focusCode();
      };
      document.addEventListener("visibilitychange", this._onVisible);
    },

    destroy() {
      if (this._onVisible) document.removeEventListener("visibilitychange", this._onVisible);
    },

    get boxes() {
      return codeBoxes(this.code);
    },

    clean() {
      this.code = cleanCode(this.code);
    },

    focusCode() {
      // $root: the field sits in signaturePad's scope, where $refs would not reach it.
      this.$nextTick(() => this.$root.querySelector("#signature_code")?.focus());
    },

    remember(method, typedName, drawing) {
      writeSaved(window.sessionStorage, storageKey, { method, typedName, drawing });
    },

    restoreInto(pad) {
      if (!restore) return;

      const saved = readSaved(window.sessionStorage, storageKey);
      if (saved?.typedName) pad.typedName = saved.typedName;
      if (saved?.method !== "drawn" || !saved.drawing?.length) return;

      pad.method = "drawn";
      pad.$nextTick(() => pad.restoreDrawing(saved.drawing));
    }
  }));
}
